import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, Button, Card, IconButton, Paper, Skeleton, Typography } from "@mui/material";
import { useState } from "react";
import { GradientStar } from "../../../styles/icons/gradient-star";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { AdditionalServicesModal } from "../../additional-services/additional-services-modal";
import { useAdditionalServiceItems } from "../../additional-services/use-additional-service-items";
import { SeverityPill } from "../../generic/components/severity-pill";

import type { AdditionalServicesType } from "../../additional-services/type";
import type { FC } from "react";

// Room for any item's title, so the tile keeps its height on load and between items.
const TILE_HEIGHT = 200;

/** New initiatives and features to highlight: the home page's "Do something special" items. */
export const WhatsNewCard: FC = () => {
  const dialog = useDialog<{ service: AdditionalServicesType }>();
  const items = useAdditionalServiceItems();
  const [index, setIndex] = useState(0);

  if (items?.length === 0) return null;

  const item = items?.[index % items.length];

  return (
    <Card sx={{ p: 2, height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 34,
          mb: 2,
        }}
      >
        <Paper
          elevation={2}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            "& svg": { width: 18, height: 18 },
          }}
        >
          <GradientStar />
          <Typography variant="subtitle2">What’s new?</Typography>
        </Paper>
        {items && items.length > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              color="primary"
              aria-label="Previous"
              data-analytics-id="dashboard-whats-new-previous"
              onClick={() => setIndex((index + items.length - 1) % items.length)}
            >
              <ChevronLeft />
            </IconButton>
            <Typography variant="subtitle2">
              {(index % items.length) + 1} / {items.length}
            </Typography>
            <IconButton
              size="small"
              color="primary"
              aria-label="Next"
              data-analytics-id="dashboard-whats-new-next"
              onClick={() => setIndex((index + 1) % items.length)}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>

      {item ? (
        <Box
          sx={[
            {
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              minHeight: TILE_HEIGHT,
              borderRadius: 2,
            },
            (theme) => ({
              bgcolor: theme.palette.mode === "dark" ? theme.palette.grey[900] : "#FED234",
            }),
          ]}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SeverityPill color="primary">{item.name}</SeverityPill>
            <Typography variant="body1" sx={{ my: 2 }}>
              {item.title}
            </Typography>
            <Button
              variant="contained"
              onClick={() => dialog.handleOpen({ service: item.service })}
              data-analytics-id={`dashboard-whats-new-${item.key}`}
            >
              {item.description}
            </Button>
          </Box>
          <Box
            component="img"
            src={item.image}
            alt=""
            sx={{ width: "45%", height: 140, objectFit: "contain" }}
          />
        </Box>
      ) : (
        <Skeleton variant="rounded" height={TILE_HEIGHT} />
      )}
      <AdditionalServicesModal dialog={dialog} />
    </Card>
  );
};
